import { gql } from '@apollo/client'
import { AiIcon, BodyText, Button, Card, Chip, Heading } from '@farol-ds/react'
import { ChipKindEnum } from '@jusbrasil-web/shared-apollo-client/types'
import classNames from 'classnames'

import { BenefitsList } from '../../../benefits-list'
import { CheckoutButton } from '../../../checkout-button'
import { LockIcon } from '../../../lock-icon'
import { OneClickButton } from '../../../one-click-button'
import { OfferBanner } from '../offer-banner'
import {
  OfferCard_OfferCardDataFragment,
  OfferCard_ViewerFragment,
} from './__generated__/offer-card.graphql'
import styles from './offer-card.module.scss'
import { OfferCardPrice } from './offer-card-price'

interface OfferCardProps {
  offerCardData: OfferCard_OfferCardDataFragment
  viewer: OfferCard_ViewerFragment | null
}

export const OfferCard = ({ offerCardData, viewer }: OfferCardProps) => {
  const {
    title,
    subtitle,
    offer,
    creditCard,
    buttons,
    icon,
    chip,
    infoChip,
    banner,
    forceCheckout,
  } = offerCardData
  const { isRecommendedOffer } = offer
  const { label: chipLabel, kind: chipKind } = chip

  const hasCreditCard = !!creditCard?.ccToken
  const kindButton = buttons?.[0]?.kind || (isRecommendedOffer ? 'primary' : 'secondary')

  return (
    <div
      className={classNames(styles.container, {
        [styles.recommendedContainer]: isRecommendedOffer,
      })}
    >
      <BodyText size="md" className={styles.recommendedText}>
        {isRecommendedOffer && 'Recomendado'}
      </BodyText>

      <Card className={styles.content}>
        <div className={classNames(styles.header, { [styles.headerWithInfoChip]: !!infoChip })}>
          <div className={styles.headerTitleContainer}>
            <div className={styles.headerTitle}>
              <LockIcon icon={icon} />
              <Heading size="md">{title}</Heading>
            </div>

            {chipLabel && (
              <Chip size="sm" kind={chipKind ?? 'tertiary'} className={styles.chip}>
                <span dangerouslySetInnerHTML={{ __html: chipLabel }} />
              </Chip>
            )}
          </div>

          <BodyText size="md">{subtitle}</BodyText>
        </div>

        {infoChip?.label && (
          <Chip
            size="md"
            kind={infoChip.kind ?? ChipKindEnum.Info}
            leftIcon={<AiIcon />}
            className={styles.infoChip}
          >
            {infoChip.label}
          </Chip>
        )}

        <OfferCardPrice offerLockOffer={offer} />

        <div className={styles.paymentButtons}>
          {viewer ? (
            <>
              <CheckoutButton kind={kindButton} offerLockCard={offerCardData} viewer={viewer} />
              {forceCheckout && hasCreditCard ? (
                <CheckoutButton
                  kind={kindButton}
                  offerLockCard={offerCardData}
                  viewer={viewer}
                  forceCheckout
                />
              ) : (
                hasCreditCard && (
                  <OneClickButton offerLockCard={offerCardData} viewer={viewer} kind={kindButton} />
                )
              )}
            </>
          ) : (
            buttons?.[0]?.url && (
              <Button kind={kindButton} asChild>
                <a href={buttons[0].url}>{buttons[0].label}</a>
              </Button>
            )
          )}
        </div>

        {banner && <OfferBanner offerLockBanner={banner} />}

        <div>
          <BenefitsList offerLockCard={offerCardData} />
        </div>
      </Card>
    </div>
  )
}

OfferCard.fragments = {
  offerCardData: gql`
    fragment OfferCard_offerCardData on OfferLockCard {
      title
      subtitle
      chip {
        label
        kind
      }
      infoChip {
        label
        kind
      }
      banner {
        ...OfferBanner_offerLockBanner
      }
      icon {
        ...LockIcon_offerLockIcon
      }
      offer {
        isRecommendedOffer
        ...OfferCardPrice_offerLockOffer
      }
      buttons {
        label
        kind
        url
      }
      forceCheckout
      ...BenefitsList_offerLockCard
      ...CheckoutButton_offerLockCard
      ...OneClickButton_offerLockCard
    }
    ${CheckoutButton.fragments.offerLockCard}
    ${OneClickButton.fragments.offerLockCard}
    ${OfferCardPrice.fragments.offerLockOffer}
    ${BenefitsList.fragments.offerLockCard}
    ${OfferBanner.fragments.offerLockBanner}
    ${LockIcon.fragments.icon}
  `,
  viewer: gql`
    fragment OfferCard_viewer on Profile {
      pid
      ...OneClickButton_viewer
      ...CheckoutButton_viewer
    }
    ${OneClickButton.fragments.viewer}
    ${CheckoutButton.fragments.viewer}
  `,
}
